import React, { createContext, useContext, useMemo } from 'react';

/**
 * Supplies `enterpriseId` to Puck custom field widgets.
 *
 * Puck's custom-field render receives only `{ field, name, value, onChange, readOnly }`, so a
 * widget that needs to fetch enterprise-scoped data (e.g. the asset library picker) has no other
 * way to learn which enterprise it is in.
 *
 * Deliberately lives in `fields/` rather than `editor/`: `cms/bundle-boundaries.test.ts` fails the
 * build if anything reachable from a renderer entry point imports an `editor`/`*-editor`
 * directory, and field widgets *are* reachable from the renderer (render-config spreads the
 * editor's Puck config, including its `fields`, into the renderer's own config).
 */
type CmsEnterpriseContextValue = { enterpriseId: string | undefined };

const CmsEnterpriseContext = createContext<CmsEnterpriseContextValue>({ enterpriseId: undefined });

export function CmsEnterpriseProvider({
  enterpriseId,
  children,
}: {
  enterpriseId: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  const value = useMemo(() => ({ enterpriseId }), [enterpriseId]);

  return <CmsEnterpriseContext.Provider value={value}>{children}</CmsEnterpriseContext.Provider>;
}

/** Undefined outside the editor -- the public renderer never mounts the provider. */
export function useCmsEnterpriseId(): string | undefined {
  return useContext(CmsEnterpriseContext).enterpriseId;
}
